<template>
  <section id="subscription-renew">
    <!-- Mobile Header -->
    <div class="custom-mobile-header d-md-none">
      <div class="header-content">
        <button
          type="button"
          class="back-button"
          @click="goBack"
        >
          <v-icon size="25" color="#fff">mdi-arrow-right-bold</v-icon>
        </button>
        <div class="header-title">تمدید اشتراک</div>
        <button type="button" class="wallet-pill" @click="$router.push({ name: 'payment-wallet' })">
          <v-icon size="16" color="#fff">mdi-wallet</v-icon>
          {{ shortToman(walletBalance) }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">در حال بارگذاری...</p>
    </div>

    <div v-else-if="loadError" class="state-box">
      <v-icon size="64" color="#94a3b8">mdi-cloud-alert</v-icon>
      <h5 class="mt-3">بارگذاری انجام نشد</h5>
      <p class="text-muted">{{ loadError }}</p>
      <b-button variant="primary" @click="initialize">تلاش دوباره</b-button>
    </div>

    <div v-else class="renew-page">
      <!-- Page Header -->
      <div class="page-top d-none d-md-flex">
        <div>
          <h3 class="mb-0">تمدید اشتراک</h3>
          <p class="text-muted mb-0">یک یا چند دستگاه را انتخاب کنید؛ تمدید بلافاصله بعد از پرداخت انجام می‌شود.</p>
        </div>
        <button type="button" class="wallet-chip" @click="$router.push({ name: 'payment-wallet' })">
          <v-icon size="20" color="#0284c7">mdi-wallet</v-icon>
          موجودی کیف پول: <b>{{ toman(walletBalance) }} تومان</b>
          <span class="plus">+</span>
        </button>
      </div>

      <div class="content-grid">
        <div>
          <!-- ۱. دستگاه‌ها -->
          <div class="section-card">
            <div class="section-header">
              <span class="step">۱</span>
              <h5 class="mb-0">دستگاه‌ها</h5>
              <span v-if="devices.length" class="header-extra">
                {{ fa(selectedIds.length) }} از {{ fa(devices.length) }} انتخاب شده
                <a v-if="filteredDevices.length > 1" href="#" @click.prevent="toggleAll">{{ allSelected ? 'برداشتن همه' : 'انتخاب همه' }}</a>
              </span>
            </div>
            <div class="section-body">
              <div v-if="devices.length > 6" class="search-box mb-3">
                <v-icon size="20" color="#94a3b8">mdi-magnify</v-icon>
                <input v-model="search" type="text" placeholder="جست‌وجو با نام، IMEI یا نام مشتری">
              </div>
              <div v-if="devices.length === 0" class="text-center py-4 text-muted">
                هیچ دستگاهی یافت نشد
              </div>
              <div
                v-for="device in visibleDevices"
                :key="device._id"
                class="device-row"
                :class="{ selected: isSelected(device) }"
                @click="toggleDevice(device)"
              >
                <span class="box">
                  <v-icon v-if="isSelected(device)" size="16" color="#fff">mdi-check-bold</v-icon>
                </span>
                <v-icon size="28" class="device-icon" :color="isSelected(device) ? '#0ea5e9' : '#64748b'">
                  {{ getDeviceIcon(device.category) }}
                </v-icon>
                <div class="device-info">
                  <div class="device-title">{{ device.title || 'دستگاه' }}</div>
                  <div class="device-meta">
                    <span class="ltr">{{ device.imei }}</span>
                    <span v-if="device.owner"> — {{ device.owner }}</span>
                  </div>
                </div>
                <div class="device-exp">
                  <b-badge :variant="stateVariant(device)" pill>{{ stateText(device) }}</b-badge>
                  <div class="exp-line">
                    انقضا: {{ fa(device.expireTime || '—') }}
                    <template v-if="selectedPlan && device.preview[selectedPlan._id]">
                      ← <span class="after">{{ fa(device.preview[selectedPlan._id].after) }}</span>
                    </template>
                  </div>
                  <div v-if="isSelected(device) && selectedPlan && device.preview[selectedPlan._id]" class="exp-hint">
                    {{ device.preview[selectedPlan._id].base === 'today' ? 'از امروز حساب می‌شود' : 'از تاریخِ انقضا ادامه پیدا می‌کند' }}
                  </div>
                </div>
              </div>
              <div v-if="filteredDevices.length > visibleDevices.length" class="text-center mt-2">
                <b-button variant="link" size="sm" @click="showAll = true">نمایش همه ({{ fa(filteredDevices.length) }})</b-button>
              </div>
            </div>
          </div>

          <!-- ۲. پلن -->
          <div class="section-card">
            <div class="section-header">
              <span class="step">۲</span>
              <h5 class="mb-0">پلن</h5>
            </div>
            <div class="section-body">
              <div v-if="plans.length === 0" class="text-center py-3 text-muted">
                هنوز پلنی با قیمت تعریف نشده است. لطفاً با پشتیبانی تماس بگیرید.
              </div>
              <div
                v-for="plan in plans"
                :key="plan._id"
                class="plan-row"
                :class="{ selected: selectedPlan && selectedPlan._id === plan._id }"
                @click="selectedPlan = plan"
              >
                <v-icon size="30" color="#0ea5e9">mdi-calendar-check</v-icon>
                <div>
                  <div class="plan-title">{{ plan.title }}</div>
                  <div class="text-muted small">{{ plan.description || `${fa(plan.period)} ماه ردیابی، هشدار و پشتیبانی` }}</div>
                </div>
                <div class="plan-price">
                  {{ toman(plan.price) }} <span class="text-muted small">تومان / دستگاه</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ۳. روش پرداخت -->
          <div v-if="selectedIds.length && selectedPlan" class="section-card">
            <div class="section-header">
              <span class="step">۳</span>
              <h5 class="mb-0">روش پرداخت</h5>
            </div>
            <div class="section-body methods">
              <div
                v-for="m in methodList"
                :key="m.key"
                class="method"
                :class="{ selected: method === m.key, disabled: !m.available }"
                @click="chooseMethod(m)"
              >
                <span class="radio" />
                <div>
                  <div class="method-title">{{ m.title }}</div>
                  <div class="text-muted small">
                    {{ m.hint }}
                    <a v-if="m.key === 'wallet' && !m.available && gatewayEnabled" href="#" @click.prevent.stop="topupShortfall">
                      {{ toman(total - walletBalance) }} تومان شارژ کنید
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- خلاصه -->
        <div class="summary-col">
          <div class="section-card summary">
            <div class="section-header">
              <v-icon size="20" color="#0f172a">mdi-receipt-text</v-icon>
              <h5 class="mb-0">خلاصهٔ پرداخت</h5>
            </div>
            <div class="section-body">
              <template v-if="selectedIds.length && selectedPlan">
                <div class="sum-row">
                  <span>{{ fa(selectedIds.length) }} دستگاه × {{ toman(selectedPlan.price) }}</span>
                  <span>{{ toman(total) }} تومان</span>
                </div>
                <div v-if="split.wallet > 0" class="sum-row">
                  <span>از کیف پول</span>
                  <span class="minus">−{{ toman(split.wallet) }} تومان</span>
                </div>
                <div class="sum-row total">
                  <span>{{ split.gateway > 0 ? 'پرداخت با کارت' : 'قابل پرداخت' }}</span>
                  <span>{{ toman(split.gateway > 0 ? split.gateway : split.wallet) }} تومان</span>
                </div>
                <b-button
                  variant="primary"
                  block
                  class="pay-button mt-3"
                  :disabled="!canPay || paymentLoading"
                  @click="pay"
                >
                  <b-spinner v-if="paymentLoading" small class="ml-2" />
                  <v-icon v-else size="20" color="#fff" class="ml-2">{{ split.gateway > 0 ? 'mdi-credit-card' : 'mdi-wallet' }}</v-icon>
                  {{ split.gateway > 0 ? 'پرداخت و تمدید' : 'تمدید با کیف پول' }}
                </b-button>
                <div v-if="!canPay" class="text-danger small text-center mt-2">{{ blockReason }}</div>
                <div v-if="split.gateway > 0" class="secure">
                  <v-icon size="16" color="#64748b">mdi-lock</v-icon>
                  پرداخت امن با درگاه شاپرکیِ بانک ملی (سداد)
                </div>
              </template>
              <div v-else class="text-muted text-center py-3">
                دستگاه و پلن را انتخاب کنید
              </div>
              <hr>
              <ul class="help-list">
                <li>اگر پرداخت ناموفق باشد، مبلغِ برداشته‌شده از کیف پول برمی‌گردد.</li>
                <li>سابقهٔ پرداخت در «تراکنش‌های من» می‌ماند.</li>
              </ul>
              <b-button
                variant="outline-primary"
                size="sm"
                block
                @click="$router.push({ name: 'payment-transactions' })"
              >
                تراکنش‌های من
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- نوارِ پرداختِ موبایل -->
      <div v-if="selectedIds.length && selectedPlan" class="mobile-paybar d-md-none">
        <div class="paybar-sum">
          <span>{{ split.wallet > 0 && split.gateway > 0 ? `کارت (${toman(split.wallet)} از کیف پول)` : 'قابل پرداخت' }}</span>
          <b>{{ toman(split.gateway > 0 ? split.gateway : split.wallet) }} تومان</b>
        </div>
        <b-button variant="primary" block :disabled="!canPay || paymentLoading" @click="pay">
          <b-spinner v-if="paymentLoading" small class="ml-2" />
          {{ split.gateway > 0 ? 'پرداخت و تمدید' : 'تمدید با کیف پول' }}
        </b-button>
      </div>
    </div>

    <!-- Footer for Mobile -->
    <footer class="mobile-footer d-md-none">
      <v-list dense>
        <v-list-item-group v-model="selectedFooterItem" color="primary">
          <v-list-item
            v-for="item in footerItems"
            :key="item.keyItem"
            @click="$router.push({ name: item.rout })"
          >
            <v-list-item-icon>
              <v-icon v-text="item.icon" />
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="item.text" />
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </footer>
  </section>
</template>

<script>
import { BButton, BBadge, BSpinner } from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// باید با MIN_GATEWAY_RIAL در سرور یکی باشد
const MIN_GATEWAY_RIAL = 10000
const PAGE = 30

export default {
  components: { BButton, BBadge, BSpinner },
  data() {
    return {
      loading: true,
      loadError: '',
      paymentLoading: false,
      gatewayEnabled: false,
      walletBalance: 0,
      devices: [],
      plans: [],
      selectedIds: [],
      selectedPlan: null,
      method: 'gateway',
      methodTouched: false,
      search: '',
      showAll: false,
      selectedFooterItem: null,
      footerItems: [
        { keyItem: 0, text: 'خانه', icon: 'mdi-home', rout: 'home' },
        { keyItem: 1, text: 'کیف پول', icon: 'mdi-wallet', rout: 'payment-wallet' },
        { keyItem: 2, text: 'پروفایل', icon: 'mdi-account', rout: 'profile' },
      ],
    }
  },
  computed: {
    filteredDevices() {
      const q = this.normalize(this.search).trim().toLowerCase()
      if (!q) return this.devices
      return this.devices.filter(d => [d.title, d.imei, d.owner].some(v => v && this.normalize(v).toLowerCase().includes(q)))
    },
    visibleDevices() {
      return this.showAll || this.search ? this.filteredDevices : this.filteredDevices.slice(0, PAGE)
    },
    allSelected() {
      return this.filteredDevices.length > 0 && this.filteredDevices.every(d => this.selectedIds.includes(d._id))
    },
    total() {
      return this.selectedPlan ? this.selectedPlan.price * this.selectedIds.length : 0
    },
    methodList() {
      const { total } = this
      const balance = this.walletBalance
      const mixed = this.splitFor('mixed')
      return [
        {
          key: 'wallet',
          title: 'فقط کیف پول',
          available: balance >= total && total > 0,
          hint: balance >= total ? `${this.toman(total)} تومان از کیف پول برداشته می‌شود` : 'موجودی کافی نیست —',
        },
        {
          key: 'mixed',
          title: 'کیف پول + درگاه بانک',
          available: this.gatewayEnabled && balance > 0 && balance < total,
          hint: balance > 0 && balance < total
            ? `${this.toman(mixed.wallet)} تومان از کیف پول و ${this.toman(mixed.gateway)} تومان با کارت`
            : (balance >= total ? 'موجودی کیف پول برای کلِ مبلغ کافی است' : 'کیف پول خالی است'),
        },
        {
          key: 'gateway',
          title: 'درگاه بانک ملی (سداد)',
          available: this.gatewayEnabled,
          hint: this.gatewayEnabled ? 'همهٔ مبلغ با کارت بانکی' : 'درگاه بانکی موقتاً غیرفعال است',
        },
      ]
    },
    split() {
      return this.splitFor(this.method)
    },
    canPay() {
      const m = this.methodList.find(x => x.key === this.method)
      return Boolean(this.selectedIds.length && this.selectedPlan && m && m.available)
    },
    blockReason() {
      if (!this.gatewayEnabled && this.walletBalance < this.total) return 'درگاه بانکی غیرفعال است و موجودی کیف پول کافی نیست'
      return 'روش پرداخت را انتخاب کنید'
    },
  },
  watch: {
    total() {
      this.pickMethod()
    },
  },
  mounted() {
    this.initialize()
  },
  methods: {
    async initialize() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await PaymentService.getOptions()
        if (res.status !== 'success') throw new Error(res.message || 'خطا در بارگذاری اطلاعات')
        const r = res.result
        this.gatewayEnabled = r.gatewayEnabled
        this.walletBalance = r.walletBalance || 0
        this.plans = r.plans || []
        // منقضی‌ها و نزدیک به انقضا اول
        const rank = {
          expired: 0, soon: 1, unknown: 2, disabled: 2, active: 3,
        }
        this.devices = (r.devices || []).slice().sort((a, b) => (rank[a.state] - rank[b.state]) || ((a.daysLeft || 0) - (b.daysLeft || 0)))
        this.selectedPlan = this.plans.find(p => this.selectedPlan && p._id === this.selectedPlan._id) || this.plans[0] || null
        const known = new Set(this.devices.map(d => d._id))
        this.selectedIds = this.selectedIds.filter(id => known.has(id))
        if (!this.selectedIds.length) this.preselect()
        this.pickMethod()
      } catch (error) {
        this.loadError = error.message || 'خطا در بارگذاری اطلاعات'
      } finally {
        this.loading = false
      }
    },
    preselect() {
      const { deviceId, imei } = this.$route.query
      const device = this.devices.find(d => (deviceId && d._id === deviceId) || (imei && d.imei === imei))
      if (device) this.selectedIds = [device._id]
      else if (this.devices.length === 1) this.selectedIds = [this.devices[0]._id]
    },
    // پیش‌فرض: هر چه بیشتر از کیف پول (کیف پول ← ترکیبی ← درگاه)، مگر کاربر خودش روشی را انتخاب کرده باشد
    pickMethod() {
      const available = this.methodList.filter(m => m.available).map(m => m.key)
      if (this.methodTouched && available.includes(this.method)) return
      this.method = available[0] || 'gateway'
    },
    chooseMethod(m) {
      if (!m.available) return
      this.method = m.key
      this.methodTouched = true
    },
    splitFor(method) {
      const { total } = this
      if (method === 'gateway') return { wallet: 0, gateway: total }
      let wallet = Math.min(this.walletBalance, total)
      let gateway = total - wallet
      if (gateway > 0 && gateway < MIN_GATEWAY_RIAL) {
        gateway = Math.min(total, MIN_GATEWAY_RIAL)
        wallet = total - gateway
      }
      return { wallet, gateway }
    },
    isSelected(device) {
      return this.selectedIds.includes(device._id)
    },
    toggleDevice(device) {
      this.selectedIds = this.isSelected(device) ? this.selectedIds.filter(id => id !== device._id) : [...this.selectedIds, device._id]
    },
    toggleAll() {
      const ids = this.filteredDevices.map(d => d._id)
      this.selectedIds = this.allSelected ? this.selectedIds.filter(id => !ids.includes(id)) : [...new Set([...this.selectedIds, ...ids])]
    },
    topupShortfall() {
      this.$router.push({ name: 'payment-wallet', query: { topup: Math.ceil((this.total - this.walletBalance) / 10) } })
    },
    async pay() {
      if (!this.canPay) return
      this.paymentLoading = true
      try {
        const res = await PaymentService.requestPayment({
          deviceIds: this.selectedIds,
          planId: this.selectedPlan._id,
          useWallet: this.method !== 'gateway',
        })
        if (res.status !== 'success') throw new Error(res.message || 'خطا در ایجاد درخواست پرداخت')
        if (res.result.paymentUrl) {
          window.location.href = res.result.paymentUrl
          return
        }
        this.$router.push({ name: 'payment-result', query: { ref: res.result.ref } })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'پرداخت انجام نشد',
            text: error.message || 'خطا در ایجاد درخواست پرداخت',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
        this.paymentLoading = false
        this.initialize()
      }
    },
    stateVariant(device) {
      return {
        expired: 'danger', soon: 'warning', active: 'success', disabled: 'secondary',
      }[device.state] || 'light'
    },
    stateText(device) {
      if (device.state === 'expired') return 'منقضی شده'
      if (device.state === 'soon') return device.daysLeft === 0 ? 'امروز تمام می‌شود' : `${this.fa(device.daysLeft)} روز مانده`
      if (device.state === 'active') return 'فعال'
      if (device.state === 'disabled') return 'غیرفعال'
      return 'بدون تاریخ'
    },
    getDeviceIcon(category) {
      const icons = {
        car: 'mdi-car',
        motorcycle: 'mdi-motorbike',
        truck: 'mdi-truck',
        bus: 'mdi-bus',
        person: 'mdi-walk',
        pet: 'mdi-paw',
      }
      return icons[category] || 'mdi-map-marker'
    },
    normalize(text) {
      return String(text || '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    },
    // ریال → تومان با رقمِ فارسی
    toman(rial) {
      return new Intl.NumberFormat('fa-IR').format(Math.round((rial || 0) / 10))
    },
    shortToman(rial) {
      const t = Math.round((rial || 0) / 10)
      if (t >= 1000000) return `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(t / 1000000)} میلیون`
      if (t >= 1000) return `${new Intl.NumberFormat('fa-IR').format(Math.round(t / 1000))} هزار`
      return new Intl.NumberFormat('fa-IR').format(t)
    },
    fa(value) {
      return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<style scoped>
#subscription-renew {
  padding-bottom: 90px;
}

.renew-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-top {
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.wallet-chip {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 6px 16px 6px 6px;
  font-family: inherit;
  color: #0f172a;
}

.wallet-chip b {
  color: #16a34a;
}

.wallet-chip .plus {
  background: #0ea5e9;
  color: #fff;
  border-radius: 999px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  font-weight: 700;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  align-items: start;
}

.section-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
}

.header-extra {
  margin-right: auto;
  font-size: 13px;
  color: #64748b;
}

.header-extra a {
  margin-right: 6px;
}

.section-body {
  padding: 16px 18px;
}

.step {
  background: #0ea5e9;
  color: #fff;
  border-radius: 999px;
  width: 24px;
  height: 24px;
  display: inline-grid;
  place-items: center;
  font-size: 13px;
  flex: none;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 12px;
}

.search-box input {
  border: 0;
  outline: 0;
  flex: 1;
  font-family: inherit;
  background: transparent;
}

.device-row {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.device-row:hover {
  border-color: #7dd3fc;
}

.device-row.selected {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.box {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #cbd5e1;
  flex: none;
  display: grid;
  place-items: center;
}

.device-row.selected .box {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.device-info {
  min-width: 0;
}

.device-title {
  font-weight: 700;
  color: #0f172a;
}

.device-meta {
  font-size: 12px;
  color: #64748b;
}

.ltr {
  display: inline-block;
  direction: ltr;
  unicode-bidi: isolate;
}

.device-exp {
  margin-right: auto;
  text-align: left;
  font-size: 12.5px;
  color: #64748b;
}

.exp-line {
  margin-top: 4px;
}

.after {
  color: #16a34a;
  font-weight: 700;
}

.exp-hint {
  font-size: 11.5px;
}

.plan-row {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 8px;
  cursor: pointer;
}

.plan-row.selected {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.plan-title {
  font-weight: 800;
  color: #0f172a;
}

.plan-price {
  margin-right: auto;
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}

.methods {
  display: grid;
  gap: 8px;
}

.method {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
}

.method.selected {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.method.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  flex: none;
  margin-top: 4px;
}

.method.selected .radio {
  border: 6px solid #0ea5e9;
}

.method-title {
  font-weight: 700;
  color: #0f172a;
}

.summary-col .summary {
  position: sticky;
  top: 90px;
}

.sum-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.sum-row.total {
  font-size: 16px;
  font-weight: 800;
  border-bottom: 0;
  border-top: 2px solid #e2e8f0;
  margin-top: 6px;
  padding-top: 10px;
  color: #0f172a;
}

.minus {
  color: #dc2626;
  font-weight: 700;
}

.pay-button {
  padding: 12px;
  font-weight: 700;
  border-radius: 12px;
}

.secure {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  margin-top: 10px;
}

.help-list {
  padding-right: 18px;
  font-size: 12.5px;
  color: #64748b;
}

.state-box {
  text-align: center;
  padding: 60px 20px;
}

.mobile-paybar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 56px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  padding: 10px 14px;
  z-index: 998;
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.06);
}

.paybar-sum {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #0f172a;
}

/* Mobile Header */
.custom-mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.custom-mobile-header .header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-mobile-header .back-button {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  padding: 5px;
}

.custom-mobile-header .header-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.wallet-pill {
  position: absolute;
  left: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.2);
  border: 0;
  border-radius: 999px;
  padding: 3px 10px;
  color: #fff;
  font-family: inherit;
  font-size: 12px;
}

/* Mobile Footer */
.mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e2e8f0;
  z-index: 999;
}

@media (max-width: 991px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .summary-col .summary {
    position: static;
  }
}

@media (max-width: 768px) {
  #subscription-renew {
    margin: -28px -28px 0;
    padding: 72px 12px 190px;
  }

  .device-row {
    flex-wrap: wrap;
    padding: 10px;
  }

  .device-icon {
    display: none;
  }

  .device-exp {
    margin-right: 34px;
    text-align: right;
    width: 100%;
  }

  .plan-row {
    flex-wrap: wrap;
  }

  .plan-price {
    margin-right: 0;
    width: 100%;
  }

  .summary-col {
    display: none;
  }
}
</style>
